/**
 * Team Table — renders the list of admin-dashboard users with per-row actions.
 *
 * Action availability is decided by caller role × target role and target status.
 * Action handlers are passed in so the parent owns mutation state + toasts.
 */

import { AlertCircle, Loader2, MoreHorizontal } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { TeamRole, TeamUser } from '@/types/team';

interface TeamTableProps {
  users: TeamUser[];
  callerRole: TeamRole;
  callerUserId: string | null;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onResend: (user: TeamUser) => void;
  onDisable: (user: TeamUser) => void;
  onEnable: (user: TeamUser) => void;
  onDelete: (user: TeamUser) => void;
  onChangeRole: (user: TeamUser, nextRole: TeamRole) => void;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function roleBadgeVariant(role: TeamRole): 'default' | 'secondary' | 'outline' {
  if (role === 'admin') return 'default';
  if (role === 'manager') return 'secondary';
  return 'outline';
}

function statusBadge(user: TeamUser) {
  if (!user.enabled) {
    return <Badge variant="destructive">Disabled</Badge>;
  }
  if (user.status === 'FORCE_CHANGE_PASSWORD') {
    return <Badge variant="outline">Invite pending</Badge>;
  }
  if (user.status === 'CONFIRMED') {
    return <Badge variant="default">Active</Badge>;
  }
  return <Badge variant="outline">{String(user.status)}</Badge>;
}

/**
 * Whether the caller is allowed to mutate a given target. Mirrors the
 * server's `assertCanActOn` + "no acting on self" rules so the UI doesn't
 * surface buttons that would 403.
 */
function canMutate(callerRole: TeamRole, callerUserId: string | null, target: TeamUser): boolean {
  if (callerUserId && target.username === callerUserId) return false;
  if (callerRole === 'admin') return true;
  if (callerRole === 'manager') return target.role === 'member';
  return false;
}

export function TeamTable({
  users,
  callerRole,
  callerUserId,
  isLoading,
  error,
  onRetry,
  onResend,
  onDisable,
  onEnable,
  onDelete,
  onChangeRole,
}: TeamTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2">Loading team…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-12">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-destructive">{error.message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No team members found.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map(user => {
          const isSelf = callerUserId !== null && user.username === callerUserId;
          const mutable = canMutate(callerRole, callerUserId, user);
          const canChangeRole = callerRole === 'admin' && !isSelf;
          const canResend = mutable && user.status === 'FORCE_CHANGE_PASSWORD';

          return (
            <TableRow key={user.username}>
              <TableCell className="font-medium">
                {user.email}
                {isSelf && (
                  <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={roleBadgeVariant(user.role)} className="capitalize">
                  {user.role}
                </Badge>
              </TableCell>
              <TableCell>{statusBadge(user)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(user.createdAt)}
              </TableCell>
              <TableCell className="text-right">
                {!mutable && !canChangeRole ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open actions menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      {canResend && (
                        <DropdownMenuItem onSelect={() => onResend(user)}>
                          Resend invitation
                        </DropdownMenuItem>
                      )}
                      {mutable && user.enabled && (
                        <DropdownMenuItem onSelect={() => onDisable(user)}>
                          Disable
                        </DropdownMenuItem>
                      )}
                      {mutable && !user.enabled && (
                        <DropdownMenuItem onSelect={() => onEnable(user)}>
                          Enable
                        </DropdownMenuItem>
                      )}
                      {canChangeRole && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            Change role
                          </DropdownMenuLabel>
                          {(['admin', 'manager', 'member'] as TeamRole[])
                            .filter(r => r !== user.role)
                            .map(role => (
                              <DropdownMenuItem
                                key={role}
                                onSelect={() => onChangeRole(user, role)}
                                className="capitalize"
                              >
                                Set as {role}
                              </DropdownMenuItem>
                            ))}
                        </>
                      )}
                      {mutable && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => onDelete(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
